import { AxiosError } from 'axios';
import { API_ROUTES } from '@/api/apiRoutes';
import { ToolVisibilityType } from '@/enums/tool-visibility-type';
import { safeAxios } from '@/services/safeAxios';

export interface ToolView {
  toolId: string;
  workflowId: string;
  visibility: ToolVisibilityType;
  name?: string | null;
  /** Both from the Create tool modal; seed the Community publish form. Null on older rows. */
  description?: string | null;
  thumbnail?: string | null;
  creator?: { displayName: string | null; photoURL: string | null } | null;
  isOwner: boolean;
  canViewWorkflow: boolean;
  /** Owner only: the workflow's "show workflow" opt-in is on. */
  exposesWorkflow?: boolean;
  orphaned: boolean;
}

export type ToolViewMetadata = Pick<ToolView, 'name' | 'description' | 'thumbnail' | 'creator'>;

export interface ToolSharedUser {
  email: string;
  uid?: string;
  displayName?: string;
  photoURL?: string;
}

export const getToolView = async (toolId: string): Promise<ToolView> => {
  const res = await safeAxios.get<ToolView>(API_ROUTES.tools.view({ toolId }));
  return res.data;
};

export const shareTool = async (toolId: string, emails: string[]): Promise<{ users: ToolSharedUser[] }> => {
  const res = await safeAxios.post<{ users: ToolSharedUser[] }>(API_ROUTES.tools.share({ toolId }), { emails });
  return res.data;
};

export const getToolSharedUsers = async (toolId: string): Promise<{ users: ToolSharedUser[] }> => {
  const res = await safeAxios.get<{ users: ToolSharedUser[] }>(API_ROUTES.tools.sharedUsers({ toolId }));
  return res.data;
};

export const setToolVisibility = async (toolId: string, visibility: ToolVisibilityType): Promise<void> => {
  await safeAxios.put(API_ROUTES.tools.visibility({ toolId }), { visibility });
};

/** Owner-only. Removes the tool and its Community listing; the workflow behind it stays. */
export const deleteTool = async (toolId: string): Promise<void> => {
  await safeAxios.delete(API_ROUTES.tools.byId({ toolId }));
};

export const deleteToolSharedUser = async (toolId: string, email: string): Promise<void> => {
  await safeAxios.delete(API_ROUTES.tools.user({ toolId, email }));
};

/** `canShare` is narrower than `toolId`: sharing a tool is creator-only, viewing it is not. */
export interface ToolByWorkflow {
  toolId: string | null;
  canShare: boolean;
}

/** Owner only. Opts the workflow in or out of being readable by everyone who can see its tool. */
export const setWorkflowToolAudience = async (workflowId: string, includesToolAudience: boolean): Promise<void> => {
  await safeAxios.put(API_ROUTES.recipes.toolAudience({ recipeId: workflowId }), { includesToolAudience });
};

// FF-gated. Resolves the tool id for a workflow, for any caller who can view the tool. The server
// answers 200 `{ toolId: null }` for the "no tool link" cases (flag off / no published tool), so the
// common no-tool response isn't an error. 403 (can't view an existing tool), 401 (guest), and a
// defensive 404 are all terminal "no tool link" states, so map them to null too.
export const getToolByWorkflow = async (workflowId: string): Promise<ToolByWorkflow | null> => {
  try {
    const res = await safeAxios.get<ToolByWorkflow>(API_ROUTES.tools.byWorkflow({ workflowId }));
    return res.data;
  } catch (error) {
    const status = error instanceof AxiosError ? error.response?.status : undefined;
    if (status === 404 || status === 403 || status === 401) {
      return null;
    }
    throw error;
  }
};
